<template>
  <BbPopup v-model="isOpened" :showCloseIcon="true" contentClass="add-dialog-to-collection">
    <template #popup-header>
      <div class="header-container">
        <BbIcon class="header-icon" name="collection" type="filled" size="small" />
        <TextBody tag="span" i18n-key="collections.addDocumentToCollection" weight="medium" :body-type="2" />
      </div>
    </template>
    <div class="search-container">
      <BbInput v-model="filterValue" size="small" :placeholder="t('collections.filterCollections')" :autofocus="true">
        <template #prepend-inner>
          <BbIcon size="small" name="search" />
        </template>
      </BbInput>
    </div>
    <div v-if="hasNoResultsForFilter" class="empty-message">
      <TextBody
        tag="span"
        i18n-key="collections.noResultsForFilter"
        :i18n-params="{ filterValue }"
        weight="medium"
        :body-type="2"
      />
    </div>
    <div v-else-if="collectionsToBeUpdated.length === 0" class="empty-message">
      <TextBody tag="span" i18n-key="collections.noCollectionsFound" weight="medium" :body-type="2" />
      <TextBody tag="span" i18n-key="collections.createOneNow" weight="medium" :body-type="2" />
      <BbBtn class="create-collection" size="small" @click="createCollection">
        <BbIcon size="small" name="plus-small" />
        <TextBody i18n-key="collections.createNewCollection" />
      </BbBtn>
    </div>
    <div v-else class="collection-list">
      <AddToCollectionCard
        v-for="collection in filteredItems"
        :key="collection.collectionId"
        :collection="collection"
        :src="validUrl(collection.thumbUrl)"
        :document="addToCollectionPopupState.document"
      />
    </div>
    <template #popup-actions>
      <div class="actions">
        <BbBtn class="create-collection" type="secondary" @click="createCollection">
          <BbIcon size="small" name="plus-small" />
          <TextBody i18n-key="collections.newCollection" />
        </BbBtn>
      </div>
    </template>
  </BbPopup>
</template>

<script>
import { computed, onMounted, ref } from 'vue';
import useCollections from '~/composables/useCollections';
import useModals from '~/composables/useModals';
import AddToCollectionCard from '~/components/conversation/collections/AddToCollectionCard';
import TextBody from '~/components/common/TextBody';
import { useI18n } from 'vue-i18n/dist/vue-i18n';
import validateUrl from '~/utils/url';

export default {
  components: { AddToCollectionCard, TextBody },

  setup(props, { emit }) {
    const { t } = useI18n();
    const { currentCollections, setCollectionsThumbs, isReadOnly } = useCollections();
    const { addToCollectionPopupState } = useModals();
    const { setCreateCollectionPopup, setAddToCollectionPopup } = useModals();

    const collectionsToBeUpdated = computed(() => currentCollections.value.filter((col) => !isReadOnly(col)));

    const isOpened = computed({
      get: () => addToCollectionPopupState.value.open,
      set: (val) => setAddToCollectionPopup({ open: val, document: {} }),
    });

    const filterValue = ref('');

    const filteredItems = computed(() =>
      collectionsToBeUpdated.value.filter((col) =>
        col.collectionName.toLowerCase().includes(filterValue.value.toLowerCase()),
      ),
    );

    const hasNoResultsForFilter = computed(() => filteredItems.value.length === 0 && !!filterValue.value.trim());

    const createCollection = () => {
      setCreateCollectionPopup({ open: true, collection: {} });
      setAddToCollectionPopup(false);
    };

    const handleFilter = () => console.log('handle filter');

    onMounted(async () => {
      await setCollectionsThumbs(collectionsToBeUpdated.value);
    });

    const validUrl = (url) => (validateUrl(url) ? url : '');

    return {
      addToCollectionPopupState,
      createCollection,
      collectionsToBeUpdated,
      filteredItems,
      emit,
      filterValue,
      handleFilter,
      hasNoResultsForFilter,
      isOpened,
      t,
      validUrl,
    };
  },
};
</script>

<style scoped lang="scss">
.header-container {
  display: flex;
  align-items: center;
  gap: $bb-spacing-2;

  .header-icon {
    background-color: $bb-neutral-1;
    color: $bb-neutral-0;
    padding: 6px;
    border-radius: 50%;
  }
}

.search-container {
  display: flex;
  width: 100%;
  gap: $bb-spacing-1;
  align-items: center;
  justify-content: center;

  :deep(.bb-input) {
    flex-grow: 1;
    .v-field--focused .v-field__outline__start,
    .v-field--focused .v-field__outline__end {
       border-color: $bb-neutral-4 !important;
    }
  }
}

.collection-list {
  display: flex;
  flex-direction: column;
  gap: $bb-spacing-6;
  justify-content: center;
  margin-top: $bb-spacing-4;
}

.empty-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 240px;
  justify-content: center;
  gap: $bb-spacing-2;

  span {
    font-size: $bb-spacing-6;
    line-height: $bb-spacing-7;
  }

  button {
    background-color: $bb-primary-3 !important;
  }
}

.actions {
  width: 100%;
  border-top: 1px solid $bb-neutral-5;
  padding: $bb-spacing-2 $bb-spacing-4;
  display: flex;

  button {
    width: 100%;
    background-color: transparent !important;
  }
}
</style>
